import {
  Avatar,
  Table as WSRTable,
  TableColumn as WSRTableColumn,
} from '@wix/design-system';
import React, {
  ComponentType,
  ReactElement,
  useCallback,
  useState,
} from 'react';
import { FiltersMap, KeyedItem } from '@wix/bex-core';
import { observer } from 'mobx-react-lite';
import { RowItem, RowKeyedItem, TableFoldersState } from '../../state';
import { ActionsCell } from '../ActionsCell';
import { PartialAllFilters } from '../../state/FoldersAndItemsCollectionsState';
import { FolderIcon } from '../FolderIcon';
import {
  CollectionTableWSRTable,
  CollectionTableWSRTableBaseProps,
  CollectionTableWSRTableProps,
} from '../CollectionTable/CollectionTableWSRTable';
import { CollectionTableContent } from '../CollectionTable/CollectionTableContent';
import {
  VirtualTableRepeater,
  VirtualTableRepeaterProps,
} from '../VirtualGridRepeater/VirtualTableRepeater';
import { VirtualGridStateProvider } from '../VirtualGridRepeater/VirtualGridStateContext';
import { TableFoldersColumn } from './TableFolders.types';
import { useOrFolderActionsCellColumn } from './useOrFolderActionsCellColumn';
import { TableFoldersInfiniteScroll } from './TableFoldersInfiniteScroll';
import {
  MultiCollectionBulkActionModalRenderProps,
  MultiCollectionBulkActionToolbar,
  MultiCollectionBulkActionToolbarRenderProps,
} from '../BulkAction';
import { TableColumn } from '../../model';
import { CollectionTableBaseCommonProps } from '../CollectionTable/CollectionTableBaseCommonProps';
import { SelectionDisabledProp } from '../../state/BulkActionToolbarState';
import { useCreateWSRColumnBase } from '../../hooks/useCreateWSRColumnBase';
import { TablePlaceholderStates } from '../TablePlaceholderStates/TablePlaceholderStates';

type UnionTableProps<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
> = CollectionTableWSRTableProps<
  RowItem<T1, T2>,
  T1,
  PartialAllFilters<F1, F2>
>;

type TableFolderWSRTableBaseProps<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
> = CollectionTableWSRTableBaseProps<
  RowItem<T1, T2>,
  T1,
  PartialAllFilters<F1, F2>
>;

export interface TableFoldersContentProps<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
> extends Omit<
      TableFolderWSRTableBaseProps<T1, F1, T2, F2>,
      | 'renderEmptyState'
      | 'actionsCell'
      | 'sticky'
      | 'topNotification'
      | 'selectionDisabled'
    >,
    CollectionTableBaseCommonProps<T1, F1> {
  dataHook?: string;
  /** A TableFoldersState instance created using [useTableFolders](/pages/cairo/?path=/story/common-hooks--usetablefolders). */
  state: TableFoldersState<T1, F1, T2, F2>;
  /** The same as [InfiniteScrollTable.columns](/pages/cairo/?path=/story/components-collection--table), with the addition of `renderFolder` property. */
  columns: TableFoldersColumn<T1, T2>[];
  /** Specific entry for rendering the item icon, defaults to simple `Avatar` (for folders a `Folder` icon is rendered). */
  iconColumn?: TableFoldersColumn<T1, T2>;

  onRowClick?: (item: T1, index: number) => void;
  /** Callback for folder row click  */
  onFolderRowClick?: (item: T2, index: number) => void;
  /** Disables row checkbox */
  selectionDisabled?: (item: T1 | T2) => void;
  /** A render function to be rendered when there're no items to show in the table */
  renderEmptyState?: () => ReactElement;
  folderActionsCell?: ActionsCell<T2, F2>;
  topNotification?: ReactElement;
  /**
   * Render a custom toolbar when more than 1 item is selected.<br>
   * The function accepts the following parameters
   *   * `isSelectAll`: Indicates whether the "Select All" checkbox is checked
   *   * `openModal`: Opens the a modal that contains the component from `bulkActionModal` render prop
   *   * `closeModal`: Closes the a modal that contains the component from `bulkActionModal` render prop
   *   * `clearSelection`: Clears all existing selection, call this after the action on the selected items was done
   *   * `openConfirmModal`: Opens a `<MessageModalLayout/>` to confirm an action about to be applied on the selected items.
   *
   * > When passed, enables `showSelection` by default.
   *
   */
  bulkActionToolbar?: MultiCollectionBulkActionToolbarRenderProps;
  /**
   * Render a modal to be opened from `bulkActionToolbar`.<br>
   * The function accepts the following parameters
   *   * `isSelectAll`: Indicates whether the "Select All" checkbox is checked
   *   * `closeModal`: Closes the a modal
   */
  bulkActionModal?: MultiCollectionBulkActionModalRenderProps;
}

function _TableFoldersContent<
  T1,
  F1 extends FiltersMap,
  T2,
  F2 extends FiltersMap,
>(props: TableFoldersContentProps<T1, F1, T2, F2>) {
  const {
    state,
    columns,
    renderEmptyState,
    renderError,
    iconColumn,
    multiLevelSorting,
    actionsCell,
    folderActionsCell,
    actionsCellWidth,
    actionsCellProps,
    filters,
    customColumns,
    views,
    bulkActionToolbar,
    bulkActionModal,
    tableGridSwitchButton,
    topNotification,
    search,
    titleBarDisplay = true,
    onRowClick,
    onFolderRowClick,
    selectionDisabled: selectionDisabledProp,
    horizontalScroll = true,
    rowVerticalPadding,
    ...rest
  } = props;

  const actionsCellColumn = useOrFolderActionsCellColumn({
    actionsCell,
    folderActionsCell,
    actionsCellWidth,
    actionsCellProps: {
      ...(actionsCellProps ?? {}),
      stickyActionCell: true,
    },
    state: state.collections,
  });

  const {
    toolbar: { selectedOrderedColumnsOrAll },
  } = state;

  const createWSRColumnBase = useCreateWSRColumnBase({ horizontalScroll });

  const finalColumns = [
    {
      key: 'cairo-avatar',
      title: '',
      width: '48px',
      ...iconColumn,
      style: undefined,
      render: ({ item }, rowNum) => {
        if (item._tag === 'folder') {
          return (
            iconColumn?.renderFolder?.(item.originalKeyedItem.item, rowNum) ?? (
              <FolderIcon data-hook="folder-icon" />
            )
          );
        }

        return (
          iconColumn?.render?.(item.originalKeyedItem.item, rowNum) ?? (
            <Avatar
              dataHook="item-avatar"
              name={state.collections.items.itemName(
                item.originalKeyedItem.item,
              )}
            />
          )
        );
      },
    },
    ...(selectedOrderedColumnsOrAll as TableFoldersColumn<T1, T2>[]).map(
      (column, index, { length }) => {
        const {
          id,
          title,
          width,
          sortable,
          infoTooltipProps,
          render,
          renderFolder,
          ...restColumn
        } = column;

        return {
          ...restColumn,
          ...createWSRColumnBase(column, index, length),
          sortDescending:
            sortable && id
              ? state.collections.query.sort.getIsSortDescending(id)
              : undefined,
          render: (row: RowKeyedItem<T1, T2>, rowNum: number) => {
            const item = row.item;
            if (item._tag === 'folder') {
              return renderFolder?.(item.originalKeyedItem.item, rowNum);
            }

            return render(item.originalKeyedItem.item, rowNum);
          },
        };
      },
    ),
    ...(actionsCellColumn ? [actionsCellColumn] : []),
  ] as WSRTableColumn<RowKeyedItem<T1, T2>>[];

  const [BodyElementType] = useState(
    () =>
      ({ children }: VirtualTableRepeaterProps) =>
        (
          <VirtualGridStateProvider value={state.virtual}>
            <VirtualTableRepeater>{children}</VirtualTableRepeater>
          </VirtualGridStateProvider>
        ),
  );

  const selectionDisabled = useCallback(
    ({ item }: KeyedItem<T1 | T2>) => selectionDisabledProp?.(item),
    [selectionDisabledProp],
  );

  if (selectionDisabled != null) {
    state.toolbar.bulk.selectionDisabledProp =
      selectionDisabled as SelectionDisabledProp;
  }

  const Table = CollectionTableWSRTable as ComponentType<
    UnionTableProps<T1, F1, T2, F2>
  >;

  const showSelection = bulkActionToolbar != null;

  const { st, classes } =
    WSRTable.dataTableRowVirtualStyle as import('@stylable/runtime').RuntimeStylesheet;

  return (
    <Table
      {...rest}
      showHeaderWhenEmpty
      state={state}
      sticky
      search={search}
      filters={filters}
      customColumns={customColumns}
      views={views}
      showSelection={showSelection}
      selectionToolbar={
        bulkActionToolbar && (
          <MultiCollectionBulkActionToolbar
            state={state.toolbar}
            bulkActionToolbar={bulkActionToolbar}
          />
        )
      }
      tableGridSwitchButton={tableGridSwitchButton}
      horizontalScroll={horizontalScroll}
      renderEmptyState={renderEmptyState}
      rowClass={st(classes.root)}
      headerClass={st(classes.thead)}
      headerRowClass={st(classes.root)}
      columns={finalColumns}
      useNewInfiniteScrollLoader
      titleBarDisplay={titleBarDisplay}
      isApplyColumnWidthStyle={true}
      BodyElementType={BodyElementType}
      onSortClick={(col: TableColumn<{}>) => {
        if (col.id != null) {
          state.collections.items.sort(
            { id: col.id, sortMode: col.sortMode },
            {
              clearResult: true,
              multiple: multiLevelSorting !== undefined,
            },
          );
        }
      }}
      onRowClick={
        (onRowClick || onFolderRowClick) &&
        (({ item }, index) => {
          if (item._tag === 'folder') {
            onFolderRowClick?.(item.originalKeyedItem.item, index);
          } else {
            onRowClick?.(item.originalKeyedItem.item, index);
          }
        })
      }
      selectionDisabled={
        selectionDisabledProp &&
        (({ item }) => selectionDisabledProp?.(item.originalKeyedItem.item))
      }
    >
      <TablePlaceholderStates
        state={state}
        renderEmptyState={renderEmptyState}
        renderError={renderError}
        topNotification={topNotification}
        rowVerticalPadding={rowVerticalPadding}
      />

      <CollectionTableContent state={state.table}>
        <TableFoldersInfiniteScroll state={state} />
      </CollectionTableContent>
    </Table>
  );
}

export const TableFoldersContent = observer(_TableFoldersContent);
